import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  Upload,
  Button,
  Space,
  List,
  Popconfirm,
  Typography,
  Row,
  Col,
} from "antd";
import {
  UploadOutlined,
  DeleteOutlined,
  PaperClipOutlined,
} from "@ant-design/icons";
import {
  DOCUMENT_AND_IMAGE_ACCEPT,
  beforeUploadDocumentOrImage,
  filterDocumentOrImageFileList,
} from "../../../utils/fileUpload";

const { TextArea } = Input;
const { Text } = Typography;

const MATERIAL_TYPES = [
  { value: "scheme", label: "Scheme of Work" },
  { value: "lesson_note", label: "Lesson Note" },
  { value: "lesson_plan", label: "Lesson Plan" },
  { value: "teaching_aid", label: "Teaching Aid" },
  { value: "practical_guide", label: "Practical Guide" },
];

const getSubjectAssignmentLabel = (assignment) => {
  const subjectName = assignment.subjectName || "General Class Materials";
  const streamName = assignment.streamName ? ` (${assignment.streamName})` : "";

  return `${subjectName} – ${assignment.className}${streamName}`;
};

const MaterialFormModal = ({
  visible,
  onClose,
  onSubmit,
  onDeleteFile,
  saving,
  deletingFileId,
  material,
  subjectTeachers,
}) => {
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState([]);
  const [selectedST, setSelectedST] = useState(null);

  const isEditing = !!material;

  useEffect(() => {
    if (visible) {
      if (isEditing) {
        const st =
          subjectTeachers.find((s) => s.id === material.subjectTeacherID) ||
          null;
        setSelectedST(st);
        form.setFieldsValue({
          title: material.title,
          description: material.description,
          type: material.type,
          acadyrID: material.acadyrID,
          termID: material.termID,
          subjectTeacherID: material.subjectTeacherID,
        });
      } else {
        form.resetFields();
        setSelectedST(null);
      }
      setFileList([]);
    }
  }, [visible, material, isEditing, form, subjectTeachers]);

  const handleSubjectChange = (value) => {
    const st = subjectTeachers.find((s) => s.id === value) || null;
    setSelectedST(st);
    if (st) {
      form.setFieldsValue({ acadyrID: st.acadyrID, termID: st.termID });
    }
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      await onSubmit(values, fileList, material?.id);
    } catch {
      // validation failed
    }
  };

  const uploadProps = {
    beforeUpload: beforeUploadDocumentOrImage,
    multiple: true,
    fileList,
    onChange: ({ fileList: newList }) =>
      setFileList(filterDocumentOrImageFileList(newList)),
    accept: DOCUMENT_AND_IMAGE_ACCEPT,
  };

  return (
    <Modal
      title={isEditing ? "Edit Material" : "New Instructional Material"}
      open={visible}
      onCancel={onClose}
      onOk={handleOk}
      okText={isEditing ? "Save Changes" : "Create"}
      confirmLoading={saving}
      width={680}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="title"
          label="Title"
          rules={[{ required: true, message: "Title is required" }]}
        >
          <Input placeholder="e.g. Mathematics Lesson Plan – Week 3" />
        </Form.Item>

        <Form.Item name="description" label="Description">
          <TextArea rows={3} placeholder="Optional notes or objectives" />
        </Form.Item>

        <Form.Item
          name="type"
          label="Material Type"
          rules={[{ required: true, message: "Select a material type" }]}
        >
          <Select placeholder="Select type" options={MATERIAL_TYPES} />
        </Form.Item>

        <Form.Item
          name="subjectTeacherID"
          label="Subject Assignment"
          rules={[{ required: true, message: "Select a subject assignment" }]}
        >
          <Select
            placeholder="Select subject / class"
            showSearch
            optionFilterProp="label"
            onChange={handleSubjectChange}
          >
            {subjectTeachers.map((s) => (
              <Select.Option
                key={s.id}
                value={s.id}
                label={getSubjectAssignmentLabel(s)}
              >
                {getSubjectAssignmentLabel(s)}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="acadyrID" hidden>
          <Input />
        </Form.Item>
        <Form.Item name="termID" hidden>
          <Input />
        </Form.Item>

        {selectedST && (
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={12}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Academic Year
              </Text>
              <div>
                <Text strong>{selectedST.academicYear}</Text>
              </div>
            </Col>
            <Col span={12}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Term
              </Text>
              <div>
                <Text strong>{selectedST.term}</Text>
              </div>
            </Col>
          </Row>
        )}

        {isEditing && material?.files?.length > 0 && (
          <Form.Item label="Existing Files" style={{ marginTop: 16 }}>
            <List
              size="small"
              dataSource={material.files}
              renderItem={(file) => (
                <List.Item
                  actions={[
                    <Popconfirm
                      title="Remove this file?"
                      onConfirm={() => onDeleteFile(material.id, file.id)}
                      okText="Remove"
                      okButtonProps={{ danger: true }}
                    >
                      <Button
                        type="text"
                        danger
                        size="small"
                        loading={deletingFileId === file.id}
                        icon={<DeleteOutlined />}
                      />
                    </Popconfirm>,
                  ]}
                >
                  <Space>
                    <PaperClipOutlined />
                    <Text>{file.filename}</Text>
                  </Space>
                </List.Item>
              )}
            />
          </Form.Item>
        )}

        <Form.Item label="Attach Files" style={{ marginTop: 16 }}>
          <Upload {...uploadProps} listType="text">
            <Button icon={<UploadOutlined />}>Select Files</Button>
          </Upload>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default MaterialFormModal;
